import type { ReactNode } from "react"
import { useEffect, useMemo } from "react"
import { Link, useLocation, useNavigate, useParams } from "react-router-dom"
import { AppSidebar } from "@/components/app-sidebar"
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { Separator } from "@/components/ui/separator"
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar"
import type { Account, Project, User } from "@/types"
import { cn } from "@/lib/utils"

type DashboardLayoutProps = {
  user: User
  account: Account
  projects: Project[]
  currentProject: Project | null
  onSignOut: () => void
  children: ReactNode
}

const getPageTitle = (pathname: string) => {
  if (pathname.endsWith("/subscribers")) return "Inscritos Push"
  if (pathname.endsWith("/segments")) return "Segmentos"
  if (pathname.endsWith("/contacts")) return "Contatos do Facebook"
  if (pathname.includes("/broadcasts/") && pathname.endsWith("/builder")) return "Construtor"
  if (pathname.includes("/workflows/") && pathname.endsWith("/builder")) return "Construtor"
  if (pathname.includes("/workflows/") && pathname.endsWith("/analytics")) return "Análises"
  if (pathname.includes("/broadcasts/") && pathname.endsWith("/run")) return "Execução"
  if (pathname.endsWith("/broadcasts")) return "Broadcasts"
  if (pathname.endsWith("/workflows")) return "Workflows"
  if (pathname.endsWith("/settings")) return "Configurações"
  return "Painel"
}

const replaceProjectSlug = (pathname: string, from: string, to: string) => {
  if (!from) return pathname
  const prefix = `/${from}`
  if (pathname.startsWith(prefix)) {
    return pathname.replace(prefix, `/${to}`)
  }
  return `/${to}`
}

const DashboardLayout = ({
  user,
  account,
  projects,
  currentProject,
  onSignOut,
  children,
}: DashboardLayoutProps) => {
  const location = useLocation()
  const navigate = useNavigate()
  const { projectSlug } = useParams<{ projectSlug: string }>()
  const pageTitle = useMemo(() => getPageTitle(location.pathname), [location.pathname])
  const isBuilderRoute = location.pathname.includes('/builder')

  useEffect(() => {
    if (!projectSlug) return
    if (projects.length === 0) return
    if (currentProject) return
    navigate(`/${projects[0].slug}`, { replace: true })
  }, [projectSlug, projects, currentProject, navigate])

  const handleSelectProject = (slug: string) => {
    if (!projectSlug) {
      navigate(`/${slug}`)
      return
    }

    const nextPath = replaceProjectSlug(location.pathname, projectSlug, slug)
    navigate(nextPath)
  }

  const handleCreateProject = () => {
    if (projects.length === 0) {
      navigate("/onboarding")
      return
    }
    if (currentProject) {
      navigate(`/${currentProject.slug}/projects`)
    } else {
      navigate("/onboarding")
    }
  }

  return (
    <SidebarProvider>
      <AppSidebar
        accountName={account.name}
        user={user}
        projects={projects}
        currentProject={currentProject}
        onSelectProject={handleSelectProject}
        onCreateProject={handleCreateProject}
        onSignOut={onSignOut}
      />
      <SidebarInset className={cn(isBuilderRoute && "overflow-hidden")}>
        {!isBuilderRoute && (
          <header className="flex h-16 shrink-0 items-center gap-2 border-b bg-background px-4">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-2 h-4" />
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to={currentProject ? `/${currentProject.slug}` : "/onboarding"}>
                      {currentProject?.name ?? "Projeto"}
                    </Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage>{pageTitle}</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </header>
        )}
        <div className={cn(
          "flex flex-1 flex-col",
          isBuilderRoute ? "overflow-hidden h-full" : "gap-6 p-6"
        )}>
          {children}
        </div>
      </SidebarInset>
    </SidebarProvider>
  )
}

export default DashboardLayout
